import { useState } from 'react';
import { contactInfo } from '@/mocks/siteData';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setStatus('success');
      return;
    }

    setStatus('loading');
    const formData = new FormData(form);
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, value.toString());
    });

    fetch('https://readdy.ai/api/form/d9rc3jknlsngrm4lma4g', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    })
      .then(async (response) => {
        const responseText = await response.text();
        let parsed: any = {};
        try {
          parsed = JSON.parse(responseText);
        } catch {
          parsed = {};
        }

        if (response.ok && parsed?.code === 'OK') {
          setStatus('success');
          form.reset();
        } else {
          const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
          if (msg.toLowerCase().includes('spam') || msg.includes('form data is spam')) {
            setErrorMsg('提交失败，请稍后再试');
          } else {
            setErrorMsg(msg);
          }
          setStatus('error');
        }
      })
      .catch(() => {
        setErrorMsg('网络错误，请检查连接后重试');
        setStatus('error');
      });
  };

  return (
    <section className="py-10 md:py-14">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
        <div>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-5">
            联系我们
          </h2>
          <p className="text-sm text-foreground-600 leading-relaxed mb-6">
            有任何问题、建议或合作意向，欢迎通过以下方式联系我们，或直接填写右侧表单。
          </p>
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <i className="ri-mail-line text-lg"></i>
              </div>
              <div>
                <p className="text-xs text-foreground-500">电子邮箱</p>
                <p className="text-sm text-foreground-800 font-medium">{contactInfo.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <i className="ri-wechat-line text-lg"></i>
              </div>
              <div>
                <p className="text-xs text-foreground-500">微信公众号</p>
                <p className="text-sm text-foreground-800 font-medium">{contactInfo.wechat}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <i className="ri-map-pin-line text-lg"></i>
              </div>
              <div>
                <p className="text-xs text-foreground-500">办公地址</p>
                <p className="text-sm text-foreground-800 font-medium">{contactInfo.address}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <i className="ri-time-line text-lg"></i>
              </div>
              <div>
                <p className="text-xs text-foreground-500">工作时间</p>
                <p className="text-sm text-foreground-800 font-medium">{contactInfo.workingHours}</p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 flex flex-col gap-4"
          >
            <div>
              <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">姓名</label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-md bg-white text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                placeholder="请输入您的姓名"
              />
            </div>

            <div>
              <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-md bg-white text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                placeholder="请输入您的邮箱"
              />
            </div>

            <div>
              <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">主题</label>
              <input
                id="contact-subject"
                name="subject"
                type="text"
                required
                className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-md bg-white text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                placeholder="请输入主题"
              />
            </div>

            <div>
              <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">留言</label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={4}
                className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-md bg-white text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                placeholder="请输入您的留言，限500字以内"
              ></textarea>
            </div>

            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="anti-spam-field"
            />

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {status === 'loading' ? '提交中...' : '发送留言'}
            </button>

            {status === 'success' && (
              <p className="text-sm text-accent-700 text-center">留言发送成功！我们会尽快与您联系。</p>
            )}
            {status === 'error' && (
              <p className="text-sm text-red-600 text-center">{errorMsg}</p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}